import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Button from '../Button/Button';
import Group from '../Group/Group';
import Menu from '../Menu/Menu';
import Metadata from '../Metadata/Metadata';
import Gravity from '../../constants/Gravity';

/**
 * `MoreFilters` is a component that shows a **More Filters* button and a filter popup when the button is clicked. It can be
 * used to add a filtering UI to sidebars etc. You can your own custom UI or have the {@link Metadata} component create a UI
 * for you if you pass the *metadataDefinitions* prop to it.
 *
 * This component wraps the {@link Menu} component and will accept all defined props for that component.
 *
 * @property {string} applyText The text to display for the **Apply** button. Defaults to "Apply filter".
 * @property {string} buttonClass The class name of the menu button. Use this prop to e.g. set the menu button class to
 * "primary" or "tertiary". * @property {node} children Your custom filters and fields.
 * @property {boolean} changed The enabled/disabled state of the "Apply" button. The button will be enabled by default when
 * *changed* is `undefined`. You may disable it by setting *changed* to a boolean value. `true` will enable the "Apply" and
 * `false` will disable it.
 * @property {string} className The class for this component. Will be appended to "core-more-filters".
 * @property {string} clearText The text to display for the **Clear** button. Defaults to "Clear".
 * @property {boolean} filterable If set to `true`, only fields that have metadata definition filterable equal to `true` will
 * be displayed. Defaults to `true` meaning that only filterable fields will be shown regardless of required and
 * defaultVisible states.
 * @property {string} gravity Sets the preferred position of the menu relative to the **More Filters** button.
 * See [`Gravity`](https://github.com/vimond/vimond-core-components/blob/master/src/constants/Gravity.js) for available
 * options. Defaults to `Gravity.SOUTH`.
 * @property {boolean} ignoreDefaultVisible If set to `true`, the fields that are `defaultVisible=true` in
 * *metadataDefinitions* will not be shown by default. Defaults to `false`.
 * @property {boolean} ignoreRequired If set to `true`, the required field in each metadata definition entry will be ignored
 * and can be removed by the user. Defaults to `false`.
 * @property {object} metadata The metadata values. Any defined values will make the associated field show in the filter
 * dialog. Multilingual fields will display the localized value if present in the *metadata* object. Defaults to {}.
 * @property {object} metadataDefinitions The metadata definitions. The component will use this data structure to determine
 * the data type, default value, etc.
 * @property {function} onApply A callback that is called when the user clicks on the **Apply** button.
 * @property {function} onChange A callback that is called when the user changes any of the metadata fields. See
 * {@link Metadata} for more details.
 * @property {function} onClear A callback that is called when the user clicks on the **Clear** button.
 * @property {function} onClick A callback that is called when the user clicks on the **More Filters** button to toggle the
 * filtering dropdown.
 * @property {object} suggestions An object of suggestions that are lazily fetched as the user types in values. This is used
 * for fields of type "AUTOCOMPLETE". The keys in *suggestions* should be the `name` of the metadata definition entry.
 * See how [VCC handles the "AUTOCOMPLETE" field type](https://github.com/vimond/vimond-cms/blob/master/src/client/common/components/Metadata.jsx).
 * @property {string} title The title of this component. Defaults to no title.
 * @see Menu, Metadata, Sidebar
 */
export default class MoreFilters extends Component {

  static displayName = 'MoreFilters';

  static propTypes = {
    applyText: PropTypes.string,
    buttonClass: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    changed: PropTypes.bool,
    children: PropTypes.node,
    className: PropTypes.string,
    clearText: PropTypes.string,
    disabled: PropTypes.bool,
    filterable: PropTypes.bool,
    gravity: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    ignoreDefaultVisible: PropTypes.bool,
    ignoreRequired: PropTypes.bool,
    metadata: PropTypes.object,
    metadataDefinitions: PropTypes.object,
    onApply: PropTypes.func,
    onChange: PropTypes.func,
    onClear: PropTypes.func,
    onClick: PropTypes.func,
    onSuggest: PropTypes.func,
    suggestions: PropTypes.object,
    title: PropTypes.string,
  };

  static defaultProps = {
    applyText: 'Apply filter',
    clearText: 'Clear',
    filterable: true,
    gravity: Gravity.SOUTH,
    ignoreDefaultVisible: true,
    ignoreRequired: true,
    metadata: {},
    title: 'More filters',
  };

  state = {};

  onApply = e => {
    this.setState({ visible: false });
    if (this.props.onApply) {
      this.props.onApply(e);
    }
  };

  onClear = e => {
    this.setState({ visible: false });
    if (this.props.onClear) {
      this.props.onClear(e);
    }
  };

  onClick = (e, visible) => {
    this.setState({ visible });
    if (this.props.onClick) {
      this.props.onClick(e);
    }
  };

  onClose = () => this.setState({ visible: false });

  render() {
    const { applyText, changed = true, children, className, clearText, ignoreDefaultVisible, filterable, ignoreRequired, metadata,
      metadataDefinitions, onChange, suggestions, onSuggest, ...rest } = this.props;
    const { visible } = this.state;
    let popup;

    if (visible) {
      const fields = metadataDefinitions && (
        <Metadata metadataDefinitions={metadataDefinitions} metadata={metadata} onChange={onChange}
          ignoreDefaultVisible={ignoreDefaultVisible} ignoreRequired={ignoreRequired} onSuggest={onSuggest}
          filterable={filterable} suggestions={suggestions} />
      );
      popup = (
        <Group className="core-more-filters-content" data-preventmenuclose>
          {children}
          {fields}
          <div className="buttons">
            <Button className="clear" onClick={this.onClear}>{clearText}</Button>
            <Button className="apply primary" onClick={this.onApply} disabled={!changed}>{applyText}</Button>
          </div>
        </Group>
      );
    }
    return (
      <Menu className={cx('core-more-filters', className)} {...rest} visible={visible} onClick={this.onClick}
        onClose={this.onClose} customContent>
        {popup}
      </Menu>
    );
  }

}
